import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { TrendingUp, TrendingDown, Sparkles, RefreshCw, Trophy, ArrowDown, AlertCircle } from 'lucide-react';
import { cn } from '../lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Phase 4 — Performance Loop widget.
 * Surfaces top/bottom-performing posts so operators can see what their audience
 * actually responds to. Empty-state gracefully invites them to connect ad
 * platforms when no data exists yet.
 */
export const PerformanceLoopWidget = ({ compact = false }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const fetchData = async (force = false) => {
    if (force) setRefreshing(true); else setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/performance-loop/summary?days=30&top_n=5`);
      setData(res.data);
    } catch (e) {
      console.error('Performance loop fetch error', e);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  if (loading) {
    return (
      <Card className="bg-white border-gray-border">
        <CardContent className="p-6 flex items-center justify-center">
          <RefreshCw className="h-5 w-5 text-emerald-brand animate-spin" />
        </CardContent>
      </Card>
    );
  }

  if (!data?.data_available) {
    return (
      <Card className="bg-white border-gray-border" data-testid="perf-loop-empty">
        <CardHeader>
          <CardTitle className="text-base font-heading flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-emerald-brand" />
            Performance Loop
            <Badge className="bg-purple-500/10 text-purple-600 text-[10px] ml-auto">New</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center flex-shrink-0">
              <TrendingUp className="h-5 w-5 text-emerald-600" />
            </div>
            <div className="flex-1">
              <p className="text-sm text-gray-700 font-medium">No performance data yet.</p>
              <p className="text-xs text-gray-500 mt-1 leading-relaxed">
                Connect your ad platforms and publish content to start the learning loop.
                Konnect will then use each campaign&apos;s real-world performance to tune the next round.
              </p>
              <div className="flex gap-2 mt-3 flex-wrap">
                <Button size="sm" variant="outline" onClick={() => window.location.href = '/settings/ad-accounts'} data-testid="perf-loop-connect-btn">
                  Connect Ad Accounts
                </Button>
                <Button size="sm" variant="ghost" onClick={() => fetchData(true)} disabled={refreshing}>
                  <RefreshCw className={cn('h-3.5 w-3.5 mr-1', refreshing && 'animate-spin')} />
                  Refresh
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  const { kpis, winners, losers, by_format, best_format } = data;

  return (
    <Card className="bg-white border-gray-border" data-testid="perf-loop-widget">
      <CardHeader className="pb-3">
        <CardTitle className="text-base font-heading flex items-center gap-2 flex-wrap">
          <Sparkles className="h-4 w-4 text-emerald-brand" />
          Performance Loop · Last 30 days
          <Badge className="bg-purple-500/10 text-purple-600 text-[10px]">Phase 4</Badge>
          <Button size="icon" variant="ghost" className="ml-auto h-6 w-6" onClick={() => fetchData(true)} disabled={refreshing} data-testid="perf-loop-refresh">
            <RefreshCw className={cn('h-3.5 w-3.5', refreshing && 'animate-spin')} />
          </Button>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* KPIs row */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          <KpiBox label="Posts" value={kpis.total_posts} />
          <KpiBox label="Impressions" value={kpis.impressions.toLocaleString()} />
          <KpiBox label="CTR" value={`${(kpis.ctr * 100).toFixed(2)}%`} />
          <KpiBox label="Avg score" value={kpis.avg_score} tone={kpis.avg_score >= 60 ? 'good' : kpis.avg_score >= 40 ? 'warn' : 'bad'} />
        </div>

        {/* Best format pill */}
        {best_format && best_format !== 'unknown' && (
          <div className="flex items-center gap-2 px-3 py-2 bg-emerald-50 rounded-lg border border-emerald-200">
            <Trophy className="h-4 w-4 text-emerald-700 flex-shrink-0" />
            <p className="text-xs text-emerald-800">
              Best-performing format: <strong className="font-semibold">{best_format}</strong>
              {by_format[best_format] && (
                <span className="text-emerald-700 ml-1">
                  (avg score {by_format[best_format].avg_score})
                </span>
              )}
            </p>
          </div>
        )}

        {!compact && winners.length > 0 && (
          <div>
            <p className="text-[10px] uppercase tracking-wide text-emerald-700 font-semibold mb-2 flex items-center gap-1">
              <TrendingUp className="h-3 w-3" />Winners
            </p>
            <div className="space-y-1.5">
              {winners.map((w, i) => <PostRow key={`${w.post_id}-${i}`} row={w} tone="win" />)}
            </div>
          </div>
        )}

        {!compact && losers.length > 0 && (
          <div>
            <p className="text-[10px] uppercase tracking-wide text-rose-700 font-semibold mb-2 flex items-center gap-1">
              <TrendingDown className="h-3 w-3" />Underperforming
            </p>
            <div className="space-y-1.5">
              {losers.map((l, i) => <PostRow key={`${l.post_id}-${i}`} row={l} tone="lose" />)}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const KpiBox = ({ label, value, tone }) => (
  <div className={cn(
    'rounded-lg border px-3 py-2',
    tone === 'good' ? 'bg-emerald-50 border-emerald-200' :
    tone === 'warn' ? 'bg-amber-50 border-amber-200' :
    tone === 'bad' ? 'bg-rose-50 border-rose-200' : 'bg-slate-50 border-slate-200'
  )}>
    <p className="text-[10px] uppercase tracking-wide text-gray-500">{label}</p>
    <p className="text-sm font-bold text-gray-900 mt-0.5">{value}</p>
  </div>
);

const PostRow = ({ row, tone }) => (
  <div className={cn(
    'flex items-center gap-2 px-2.5 py-1.5 rounded text-xs',
    tone === 'win' ? 'bg-emerald-50/50' : 'bg-rose-50/50'
  )}>
    {tone === 'win'
      ? <Trophy className="h-3 w-3 text-emerald-600 flex-shrink-0" />
      : <ArrowDown className="h-3 w-3 text-rose-600 flex-shrink-0" />}
    <span className="text-gray-700 truncate flex-1">{row.content_excerpt || row.post_id}</span>
    <Badge variant="outline" className="text-[10px] font-mono">{row.platform}</Badge>
    <span className={cn(
      'text-[10px] font-bold flex-shrink-0 w-10 text-right',
      tone === 'win' ? 'text-emerald-700' : 'text-rose-700'
    )}>{row.score}</span>
  </div>
);

export default PerformanceLoopWidget;
